import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Moves focus to `ref` when `isOpen` becomes true and restores focus to the
 * previously focused element when it becomes false (or the consumer unmounts).
 * Intended for non-modal dialogs/popovers — the target element must be
 * focusable (e.g. `tabIndex={-1}`).
 */
export const useFocusOnOpen = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  isOpen: boolean
): void => {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;

    ref.current?.focus();

    return () => {
      if (previouslyFocused?.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [ref, isOpen]);
};
